import { createAsyncThunk, current } from "@reduxjs/toolkit";
import * as API from "../../services/chinaTrackerService";
import { getUserInfo } from "../../services/usersService";

export function parseDDMMYYYY(dateStr: string) {
  const [day, month, year] = dateStr.split("/").map(Number);
  return new Date(year, month - 1, day);
}

// get all tracked orders from SAPI
export const getAllTrackerOrders = createAsyncThunk(
  "chinaTracker/orders/tracker",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.getAllChinaTrackerOrders();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// get all order details with shipments
export const getAllChinaTrackerDetailsWithShipments = createAsyncThunk(
  "chinaTracker/orders/tracker/details/shipments",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } =
        await API.getAllChinaTrackerDetailsWithShipments(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// get all tracker order details from SAPI
export const getTrackerOrderDetails = createAsyncThunk(
  "chinaTracker/orders/trackerplus",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.getChinaTrackerDetails();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

async function waitForMinTime<T>(
  promise: Promise<T>,
  delayMs: number,
): Promise<T> {
  return Promise.all([
    promise,
    new Promise<void>((res) => setTimeout(res, delayMs)),
  ]).then(([result]) => result);
}

export const getMergedTrackerOrders = createAsyncThunk(
  "chinaTracker/orders/merged",
  async ({ refresh }: { refresh: boolean }, { rejectWithValue }) => {
    try {
      const { data } = await API.getMergedTrackerOrders(refresh);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// get all tracker enquiries by key
export const getTrackerFlagByKey = createAsyncThunk(
  "chinaTracker/orders/flags/key",
  async (key: string, { rejectWithValue }) => {
    try {
      const { data } = await API.getTrackerFlagByKey(key);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// get all tracker interrupts by key
export const getTrackerInterruptsByKey = createAsyncThunk(
  "chinaTracker/orders/interrupts/key",
  async (key: string, { rejectWithValue }) => {
    try {
      const { data } = await API.getTrackerInterruptsByKey(key);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAllFactoryFlags = createAsyncThunk(
  "chinaTracker/orders/flags",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.getAllFactoryFlags();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const flagTrackerOrder = createAsyncThunk(
  "chinaTracker/orders/flag/add",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.flagTrackerOrder(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getFactoryOrderDetailsByKey = createAsyncThunk(
  "chinaTracker/orders/details/key",
  async (key: string, { rejectWithValue }) => {
    try {
      const { data } = await API.getFactoryOrderDetailsByKey(key);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAllFactoryOrderDetails = createAsyncThunk(
  "chinaTracker/orders/details/all",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.getAllFactoryOrderDetails();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addFactoryOrderDetails = createAsyncThunk(
  "chinaTracker/orders/details/add",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.addFactoryOrderDetails(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export interface UpdateFactoryOrderDetailsArgs {
  orderId: string;
  payload: any;
}

export const updateFactoryOrderDetails = createAsyncThunk<
  any,
  UpdateFactoryOrderDetailsArgs,
  { rejectValue: any } // optional reject type
>(
  "chinaTracker/orders/details/update",
  async ({ orderId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await API.updateFactoryOrderDetails(orderId, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateChinaOrderPriority = createAsyncThunk(
  "chinaTracker/orders/details/update/priority",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.updateChinaOrderPriority(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// get all tracker enquiries by key
export const getTrackerEnquiriesByKey = createAsyncThunk(
  "chinaTracker/orders/enquiries",
  async (key: string, { rejectWithValue }) => {
    try {
      const { data } = await API.getTrackerEnquiriesByKey(key);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAllChinaTrackerEnquiries = createAsyncThunk(
  "chinaTracker/orders/enquiries/all",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.getAllChinaTrackerEnquiries();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const searchTrackerEnquiriesByKey = createAsyncThunk(
  "chinaTracker/orders/enquiries/search",
  async ({ key, search }: any, { rejectWithValue }) => {
    try {
      const { data } = await API.searchTrackerEnquiriesByKey(key, search);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const searchFactoryEnquiriesByKey = createAsyncThunk(
  "chinaTracker/orders/flags/search",
  async ({ key, search }: any, { rejectWithValue }) => {
    try {
      const { data } = await API.searchTrackerFlagsByKey(key, search);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const searchFactoryInterruptsByKey = createAsyncThunk(
  "chinaTracker/orders/interrupts/search",
  async ({ key, search }: any, { rejectWithValue }) => {
    try {
      const { data } = await API.searchTrackerInterruptsByKey(key, search);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addTrackerOrderEnquiry = createAsyncThunk(
  "chinaTracker/orders/enquiries/add",
  async (payload: any, { rejectWithValue }) => {
    try {
      const userInfo = await getUserInfo();

      var httpBody = {
        createdBy: `${userInfo.given_name} ${userInfo.family_name}`,
        ...payload,
      };
      const { data } = await API.addTrackerEnquiry(httpBody);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addTrackerOrderEnquiryReply = createAsyncThunk(
  "chinaTracker/orders/enquiries/reply",
  async (payload: any, { rejectWithValue }) => {
    try {
      const userInfo = await getUserInfo();

      var httpBody = {
        createdBy: `${userInfo.given_name} ${userInfo.family_name}`,
        ...payload,
      };
      const { data } = await API.addTrackerEnquiry(httpBody);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const approveIncomingOrder = createAsyncThunk(
  "chinaTracker/orders/incoming/add",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.approveIncomingOrder(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const approveCancelledOrder = createAsyncThunk(
  "chinaTracker/orders/cancelled/add",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.approveCancelledOrderBulk(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const approveSingleIncomingOrder = createAsyncThunk(
  "chinaTracker/orders/incoming/add/single",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.approveSingleIncomingOrder(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateIncomingOrder = createAsyncThunk(
  "chinaTracker/orders/incoming/update",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.updateIncomingOrder(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateBulkIncomingOrder = createAsyncThunk(
  "chinaTracker/orders/incoming/update/bulk",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.updateBulkIncomingOrder(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAllChinaOrganizationalUsers = createAsyncThunk(
  "chinaTracker/organizational-users",
  async (_, { rejectWithValue }) => {
    try {
      return await API.getChinaOrganizationalUsers();
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAllCancelledOrders = createAsyncThunk(
  "chinaTracker/cancelled-orders",
  async (_, { rejectWithValue }) => {
    try {
      return await API.getAllChinaCancelledOrders();
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateChinaChecklist = createAsyncThunk(
  "chinaTracker/orders/checklist/update",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.updateChinaChecklist(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchRejectedHistoryChinaOrder = createAsyncThunk(
  "chinaTracker/orders/rejected/history",
  async (key: string, { rejectWithValue }) => {
    try {
      const { data } = await API.fetchRejectedHistoryChinaOrder(key);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchAllRejectedHistoryChinaOrder = createAsyncThunk(
  "chinaTracker/orders/rejected/history/all",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.fetchAllRejectedHistoryChinaOrder();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getChinaTrackerShipmentDetailsByDetailId = createAsyncThunk(
  "chinaTracker/orders/shipments/detail/id",
  async (factoryOrderDetailId: string, { rejectWithValue }) => {
    try {
      const { data } =
        await API.getChinaTrackerShipmentDetailsByDetailId(
          factoryOrderDetailId,
        );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createFactoryRunShipment = createAsyncThunk(
  "chinaTracker/orders/shipments/detail/create",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.createFactoryRunShipment(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateFactoryRunShipment = createAsyncThunk(
  "chinaTracker/orders/shipments/detail/update",
  async ({ payload, _id }: { payload: any; _id: any }, { rejectWithValue }) => {
    try {
      const { data } = await API.updateFactoryRunShipment(payload, _id);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createDirectFactoryRunShipment = createAsyncThunk(
  "chinaTracker/orders/shipments/detail/create/direct",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.createDirectFactoryRunShipment(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteFactoryRunShipment = createAsyncThunk(
  "chinaTracker/orders/shipments/detail/delete",
  async (_id: string, { rejectWithValue }) => {
    try {
      const { data } = await API.deleteFactoryRunShipment(_id);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Run Order Interruption Or UnInterruption
export const interruptRunOrderDetail = createAsyncThunk(
  "chinaTracker/orders/run-orders/interrupt",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.interruptRunOrderDetail(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);
